import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../api";
import { PageTitle, TH, TD, Badge, ROLE_RANK } from "./AdminLayout";
import { useAuth } from "../../context/AuthContext";

const ROLE_TONE = { owner: "amber", admin: "blue", moderator: "cyan", support: "grey" };

export default function Staff() {
  const { user } = useAuth();
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "support" });
  const isOwner = user.role === "owner";

  const load = () => api.get("/admin/staff").then((r) => setStaff(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/staff", form);
      toast.success(`${form.email} added as ${form.role}`);
      setForm({ email: "", name: "", password: "", role: "support" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const patch = async (id, body) => {
    try {
      await api.patch(`/admin/staff/${id}`, body);
      toast.success("Staff updated");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Remove ${s.email} from staff?`)) return;
    try {
      await api.delete(`/admin/staff/${id_(s)}`);
      toast.success("Removed");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const id_ = (s) => s.id;

  return (
    <div data-testid="admin-staff">
      <PageTitle title="Staff & Permissions" sub="Roles gate every admin route. Owner-only actions are enforced server-side." />

      <div className="rounded-lg border border-white/5 bg-panel overflow-x-auto mb-8">
        <table className="w-full min-w-[760px]" data-testid="staff-table">
          <thead className="border-b border-white/5"><tr><TH>Name</TH><TH>Email</TH><TH>Role</TH><TH>Last login</TH><TH>Discord ID</TH><TH>Status</TH>{isOwner && <TH />}</tr></thead>
          <tbody className="divide-y divide-white/5">
            {staff.map((s) => (
              <tr key={s.id} data-testid={`staff-row-${s.email}`}>
                <TD className="font-semibold text-white">{s.name}</TD>
                <TD className="font-mono text-xs">{s.email}</TD>
                <TD>
                  {isOwner && s.role !== "owner" ? (
                    <select value={s.role} onChange={(e) => patch(s.id, { role: e.target.value })} data-testid={`staff-role-${s.email}`}
                      className="bg-white/5 border border-white/10 rounded-md px-2 py-1 text-xs text-white focus:outline-none [&>option]:bg-panel">
                      <option value="support">support</option>
                      <option value="moderator">moderator</option>
                      <option value="admin">admin</option>
                    </select>
                  ) : (
                    <Badge tone={ROLE_TONE[s.role]}>{s.role}</Badge>
                  )}
                </TD>
                <TD className="font-mono text-xs text-white/40">{s.last_login ? new Date(s.last_login).toLocaleString() : "never"}</TD>
                <TD>
                  {isOwner ? (
                    <input
                      defaultValue={s.discord_id || ""}
                      placeholder="Discord user ID"
                      onBlur={(e) => e.target.value.trim() !== (s.discord_id || "") && patch(s.id, { discord_id: e.target.value.trim() })}
                      data-testid={`staff-discord-id-${s.email}`}
                      className="w-36 bg-white/5 border border-white/10 rounded-md px-2 py-1 text-xs font-mono text-white focus:outline-none focus:ring-2 focus:ring-blurple"
                    />
                  ) : (
                    <span className="font-mono text-xs text-white/40">{s.discord_id || "—"}</span>
                  )}
                </TD>
                <TD><Badge tone={s.banned ? "red" : "green"}>{s.banned ? "suspended" : "active"}</Badge></TD>
                {isOwner && (
                  <TD>
                    {s.role !== "owner" && (
                      <div className="flex gap-2">
                        <button onClick={() => patch(s.id, { banned: !s.banned })} className="text-xs font-semibold text-amber-400 border border-amber-400/30 rounded-full px-3 py-1 hover:bg-amber-400/10 transition-colors duration-150" data-testid={`staff-suspend-${s.email}`}>
                          {s.banned ? "Reinstate" : "Suspend"}
                        </button>
                        <button onClick={() => remove(s)} className="p-1.5 text-white/40 hover:text-red-400 transition-colors duration-150" data-testid={`staff-remove-${s.email}`} aria-label="Remove"><Trash2 size={14} /></button>
                      </div>
                    )}
                  </TD>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isOwner && (
        <form onSubmit={create} className="rounded-lg border border-white/5 bg-panel p-6 max-w-xl space-y-4" data-testid="staff-create-form">
          <p className="text-[11px] uppercase tracking-[0.18em] text-white/40 font-semibold flex items-center gap-2"><Plus size={13} /> Add staff member</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="Display name" data-testid="staff-name-input"
              className="bg-white/5 border border-white/10 rounded-md px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blurple" />
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required placeholder="Email" data-testid="staff-email-input"
              className="bg-white/5 border border-white/10 rounded-md px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blurple" />
            <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={8} placeholder="Temp password (8+ chars)" data-testid="staff-password-input"
              className="bg-white/5 border border-white/10 rounded-md px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blurple" />
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="staff-role-select"
              className="bg-white/5 border border-white/10 rounded-md px-4 py-2.5 text-sm text-white focus:outline-none [&>option]:bg-panel">
              <option value="support">support — tickets, read-only</option>
              <option value="moderator">moderator — + reviews, moderation</option>
              <option value="admin">admin — + content, settings</option>
            </select>
          </div>
          <button type="submit" className="bg-blurple hover:bg-[#4752c4] text-white font-semibold px-7 py-2.5 rounded-full text-sm transition-colors duration-150" data-testid="staff-create-button">Add to team</button>
        </form>
      )}
      {!isOwner && <p className="text-xs text-white/30 font-mono">Only the owner can add, suspend or remove staff.</p>}
    </div>
  );
}
